import React, { useState, useEffect, useRef } from 'react';
import type { YoutubeChannel, YoutubeVideo, YoutubeSyncResponse } from '../types';
import { VideoTable } from './VideoTable';
import { TubePlayer } from '@jburnhams/tube-ts';

interface SearchResult {
    videos: YoutubeVideo[];
    limit: number;
    offset: number;
    total: number;
}

interface ChannelOption {
    youtube_id: string;
    title: string;
}

interface YoutubeViewerProps {
    sessionId?: string | null;
}

export function YoutubeViewer({ sessionId }: YoutubeViewerProps = {}) {
    // Mode switcher
    const [viewMode, setViewMode] = useState<'id' | 'search' | 'channels'>('id');

    // ID Fetch State
    const [id, setId] = useState('');
    const [type, setType] = useState<'channel' | 'video'>('video');
    const [singleData, setSingleData] = useState<YoutubeChannel | YoutubeVideo | null>(null);

    // Search State
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedChannel, setSelectedChannel] = useState('');
    const [channels, setChannels] = useState<ChannelOption[]>([]);
    const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
    const [sortConfig, setSortConfig] = useState<{ by: string; order: 'asc' | 'desc' }>({ by: 'published_at', order: 'desc' });
    const [pagination, setPagination] = useState({ limit: 10, offset: 0 });

    // Common State
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Sync state
    const [syncing, setSyncing] = useState(false);
    const [syncProgress, setSyncProgress] = useState<YoutubeSyncResponse | null>(null);
    const [syncRange, setSyncRange] = useState<{ start: string | null; end: string | null }>({ start: null, end: null });
    const [totalFetched, setTotalFetched] = useState(0);
    const [selectedChannels, setSelectedChannels] = useState<Set<string>>(new Set());
    const [bulkSyncing, setBulkSyncing] = useState(false);
    const [bulkSyncStatus, setBulkSyncStatus] = useState<{ current: string; progress: string } | null>(null);

    // Player State
    const playerRef = useRef<HTMLDivElement>(null);
    const playerInstance = useRef<TubePlayer | null>(null);

    useEffect(() => {
        const isChannel = singleData && 'custom_url' in singleData;

        if (singleData && !isChannel && playerRef.current) {
            const initPlayer = async () => {
                try {
                    if (playerInstance.current) {
                        playerInstance.current.destroy();
                    }

                    // Ensure we have a unique ID for the container if not present
                    if (!playerRef.current?.id) {
                        playerRef.current!.id = `tube-player-${singleData.youtube_id}`;
                    }

                    const player = new TubePlayer(playerRef.current!.id);
                    playerInstance.current = player;

                    await player.initialize({
                        cache: true,
                        sessionId: sessionId || undefined
                    });
                    await player.loadVideo(singleData.youtube_id);
                } catch (e) {
                    console.error("TubePlayer failed to initialize/load", e);
                }
            };
            initPlayer();

            return () => {
                if (playerInstance.current) {
                    playerInstance.current.destroy();
                    playerInstance.current = null;
                }
            };
        }
    }, [singleData]);

    const fetchChannelDetail = async (channelId: string) => {
        setLoading(true);
        setError(null);
        setSingleData(null);
        // Ensure UI state matches
        setViewMode('id');
        setType('channel');
        setId(channelId);

        try {
            const res = await fetch(`/api/youtube/channel/${channelId}`);
            const json = await res.json();

            if (!res.ok) {
                throw new Error(json.message || json.error || 'Failed to fetch');
            }

            setSingleData(json);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchVideoDetail = async (videoId: string) => {
        setLoading(true);
        setError(null);
        setSingleData(null);
        // Ensure UI state matches
        setViewMode('id');
        setType('video');
        setId(videoId);

        try {
            const res = await fetch(`/api/youtube/video/${videoId}`);
            const json = await res.json();

            if (!res.ok) {
                throw new Error(json.message || json.error || 'Failed to fetch');
            }

            setSingleData(json);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleIdSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;

        setLoading(true);
        setError(null);
        setSingleData(null);

        try {
            const res = await fetch(`/api/youtube/${type}/${id}`);
            const json = await res.json();

            if (!res.ok) {
                throw new Error(json.message || json.error || 'Failed to fetch');
            }

            setSingleData(json);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchChannels = async () => {
        try {
            const res = await fetch('/api/youtube/channels');
            if (res.ok) {
                const data = await res.json();
                if (data && Array.isArray(data.channels)) {
                    setChannels(data.channels);
                } else {
                    setChannels([]);
                }
            } else {
                setChannels([]);
            }
        } catch (e) {
            console.error('Failed to fetch channels', e);
            setChannels([]);
        }
    };

    useEffect(() => {
        if (viewMode === 'search' && channels.length === 0) {
            fetchChannels();
        }
    }, [viewMode]);

    const handleSort = (column: string) => {
        setSortConfig(prev => {
            const isSame = prev.by === column;
            const newOrder = isSame && prev.order === 'desc' ? 'asc' : 'desc';
            return { by: column, order: newOrder };
        });
    };

    // Effect to trigger search when sort changes
    useEffect(() => {
        if (viewMode === 'search') {
            handleVideoSearch();
        }
    }, [sortConfig]); // Intentionally leave out pagination to handle it manually or add it if we want auto-refetch

    const totalPages = searchResults ? Math.ceil(searchResults.total / pagination.limit) : 0;
    const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newLimit = parseInt(e.target.value);
        setPagination(prev => ({ ...prev, limit: newLimit, offset: 0 }));
        handleVideoSearch({ limit: newLimit, offset: 0 }); // Pass limit directly to ensure it uses new value
    };

    const handleVideoSearch = async (opts?: { offset?: number, limit?: number, channelId?: string, query?: string }) => {
        setLoading(true);
        setError(null);

        const currentOffset = opts?.offset !== undefined ? opts.offset : pagination.offset;
        const currentLimit = opts?.limit !== undefined ? opts.limit : pagination.limit;
        const currentChannel = opts?.channelId !== undefined ? opts.channelId : selectedChannel;
        const currentQuery = opts?.query !== undefined ? opts.query : searchQuery;

        try {
            const params = new URLSearchParams();
            if (currentQuery) params.append('title_contains', currentQuery);
            if (currentChannel) params.append('channel_id', currentChannel);
            params.append('sort_by', sortConfig.by);
            params.append('sort_order', sortConfig.order);
            params.append('limit', currentLimit.toString());
            params.append('offset', currentOffset.toString());

            const res = await fetch(`/api/youtube/videos?${params.toString()}`);
            const json = await res.json();

            if (!res.ok) {
                throw new Error(json.message || json.error || 'Failed to search');
            }

            setSearchResults(json);

            // Update pagination state
            setPagination(prev => ({
                ...prev,
                limit: currentLimit,
                offset: currentOffset
            }));
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async () => {
        if (!singleData || !('custom_url' in singleData)) return;
        setSyncing(true);
        setTotalFetched(0);
        setSyncProgress(null);
        setSyncRange({ start: null, end: null });
        setError(null);

        let isComplete = false;

        try {
            let safetyLimit = 100;
            while (!isComplete && safetyLimit > 0) {
                safetyLimit--;
                const res = await fetch(`/api/youtube/channel/${singleData.youtube_id}/sync`, { method: 'POST' });
                const json = await res.json();
                if (!res.ok) throw new Error(json.message || json.error || 'Sync failed');

                const progress = json as YoutubeSyncResponse;
                setSyncProgress(progress);
                setTotalFetched(prev => prev + progress.count);

                // Update accumulated range
                setSyncRange(prev => {
                    let start = prev.start;
                    let end = prev.end;

                    if (progress.range_start) {
                        if (!start || progress.range_start < start) start = progress.range_start;
                    }
                    if (progress.range_end) {
                        if (!end || progress.range_end > end) end = progress.range_end;
                    }
                    return { start, end };
                });

                isComplete = progress.is_complete;
                if (isComplete) break;
            }
        } catch (err: any) {
            setError(`Sync error: ${err.message}`);
        } finally {
            setSyncing(false);
            try {
                const res = await fetch(`/api/youtube/channel/${singleData.youtube_id}`);
                if (res.ok) setSingleData(await res.json());
            } catch (e) { console.error(e); }
        }
    };

    const formatJSON = (jsonString: string) => {
        try { return JSON.stringify(JSON.parse(jsonString), null, 2); } catch (e) { return jsonString; }
    };

    const renderSyncProgress = () => {
        if (!syncProgress && !syncing) return null;

        return (
            <div style={{ background: 'var(--color-bg)', padding: '1rem', borderRadius: '4px', marginTop: '1rem', border: '1px solid var(--color-border)' }}>
                <h3>Sync Progress</h3>
                {syncing && <div style={{ color: 'var(--color-primary)' }}>Syncing...</div>}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginTop: '0.5rem' }}>
                    <div><strong>Total Fetched This Run:</strong> {totalFetched}</div>
                    {syncProgress && <div><strong>Total Stored Videos:</strong> {syncProgress.total_stored_videos}</div>}
                </div>
                {syncProgress?.sample_video && (
                    <div style={{ marginTop: '1rem' }}>
                        <strong>Sample Video from Sync:</strong>
                        <VideoTable
                            videos={[syncProgress.sample_video]}
                            onVideoClick={fetchVideoDetail}
                            onChannelClick={fetchChannelDetail}
                        />
                    </div>
                )}
                {syncProgress?.is_complete && <div style={{ marginTop: '1rem', color: 'green', fontWeight: 'bold' }}>Sync Complete!</div>}
            </div>
        );
    };

    const handleBulkSync = async () => {
        if (selectedChannels.size === 0) return;

        setBulkSyncing(true);
        setBulkSyncStatus({ current: '', progress: `Starting sync for ${selectedChannels.size} channels...` });

        const channelsToSync = channels.filter(c => selectedChannels.has(c.youtube_id));
        let processed = 0;

        for (const channel of channelsToSync) {
            setBulkSyncStatus({ current: channel.title, progress: `Syncing ${processed + 1}/${channelsToSync.length}` });

            // Reuse sync logic but for specific channel
            let channelComplete = false;
            let safetyLimit = 100;

            try {
                while (!channelComplete && safetyLimit > 0) {
                    safetyLimit--;
                    const res = await fetch(`/api/youtube/channel/${channel.youtube_id}/sync`, { method: 'POST' });
                    if (!res.ok) break; // Skip on error

                    const progress = await res.json() as YoutubeSyncResponse;
                    channelComplete = progress.is_complete;
                }
            } catch (e) {
                console.error(`Failed to sync channel ${channel.title}`, e);
            }
            processed++;
        }

        setBulkSyncing(false);
        setBulkSyncStatus(null);
        setSelectedChannels(new Set()); // Clear selection
        fetchChannels(); // Refresh data
        alert('Bulk sync completed!');
    };

    const toggleChannelSelection = (id: string) => {
        const newSelection = new Set(selectedChannels);
        if (newSelection.has(id)) {
            newSelection.delete(id);
        } else {
            newSelection.add(id);
        }
        setSelectedChannels(newSelection);
    };

    const handleSelectAll = () => {
        setSelectedChannels(new Set(channels.map(c => c.youtube_id)));
    };

    const handleDeselectAll = () => {
        setSelectedChannels(new Set());
    };

    const handleDeleteChannel = async () => {
        if (!singleData || !('custom_url' in singleData)) return;
        const channel = singleData as YoutubeChannel;

        if (!window.confirm(`Are you sure you want to delete channel "${channel.title}" and all its videos?`)) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await fetch(`/api/youtube/channel/${channel.youtube_id}`, { method: 'DELETE' });
            if (!res.ok) {
                const json = await res.json();
                throw new Error(json.message || json.error || 'Failed to delete channel');
            }

            // Clear view and search results if they match
            setSingleData(null);
            setId('');
            if (viewMode === 'search' && selectedChannel === channel.youtube_id) {
                setSelectedChannel('');
                setSearchResults(null);
            }

            // Re-fetch channel list to remove it from dropdown
            fetchChannels();

        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleResyncChannel = async () => {
        if (!singleData || !('custom_url' in singleData)) return;
        const channel = singleData as YoutubeChannel;
        const channelId = channel.youtube_id;

        if (!window.confirm(`Resync will delete "${channel.title}" and all its videos, then re-fetch the channel details. Continue?`)) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // 1. Delete
            const delRes = await fetch(`/api/youtube/channel/${channelId}`, { method: 'DELETE' });
            if (!delRes.ok) {
                const json = await delRes.json();
                throw new Error(json.message || json.error || 'Failed to delete channel for resync');
            }

            // 2. Re-fetch Channel (this will re-create it in DB via read-through)
            // We set singleData to null first to show loading state properly if we wanted,
            // but fetchChannelDetail does that.

            await fetchChannelDetail(channelId);

            // Re-fetch channel list to ensure it's up to date (though it was just recreated)
            fetchChannels();

        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const renderSingleResult = () => {
        if (!singleData) return null;
        const isChannel = 'custom_url' in singleData;

        let statsDisplay: { label: string, value: string }[] = [];

        if (isChannel) {
            const channel = singleData as YoutubeChannel;
            statsDisplay = [
                { label: 'Subscribers', value: channel.subscriber_count?.toLocaleString() || 'N/A' },
                { label: 'Videos', value: channel.video_count?.toLocaleString() || 'N/A' },
                { label: 'Views', value: channel.view_count?.toLocaleString() || 'N/A' }
            ];
        } else {
            const video = singleData as YoutubeVideo;
            statsDisplay = [
                { label: 'Views', value: video.view_count?.toLocaleString() || 'N/A' },
                { label: 'Likes', value: video.like_count?.toLocaleString() || 'N/A' },
                { label: 'Comments', value: video.comment_count?.toLocaleString() || 'N/A' }
            ];
        }

        return (
            <div className="youtube-result" style={{ marginTop: '2rem' }}>
                <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
                    {isChannel ? (
                        <img src={singleData.thumbnail_url} alt={singleData.title} style={{ width: '200px', borderRadius: '8px' }} />
                    ) : (
                        <div
                            ref={playerRef}
                            style={{
                                width: '400px',
                                height: '225px',
                                background: '#000',
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}
                        />
                    )}
                    <div>
                        <h2 style={{ marginBottom: '0.5rem' }}>{singleData.title}</h2>
                        <div style={{ color: 'var(--color-text-dim)', marginBottom: '1rem' }}>
                            {isChannel ? (
                                'Channel'
                            ) : (
                                <>
                                    Video by{' '}
                                    <button
                                        onClick={() => fetchChannelDetail((singleData as YoutubeVideo).channel_id)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            color: 'var(--color-primary)',
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            fontSize: 'inherit',
                                            fontFamily: 'inherit'
                                        }}
                                    >
                                        {(singleData as YoutubeVideo).channel_title || (singleData as YoutubeVideo).channel_id}
                                    </button>
                                </>
                            )}{' '}
                            • {new Date(singleData.published_at).toLocaleDateString()}
                        </div>
                        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                            {statsDisplay.map(({ label, value }) => (
                                <div key={label} style={{ background: 'var(--color-bg)', padding: '0.5rem', borderRadius: '4px' }}>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-dim)', textTransform: 'capitalize' }}>{label}</div>
                                    <div style={{ fontWeight: 'bold' }}>{value}</div>
                                </div>
                            ))}
                        </div>
                        {isChannel && (
                            <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--color-text-dim)' }}>
                                <div><strong>Sync Status:</strong></div>
                                <div>Earliest: {(singleData as YoutubeChannel).sync_start_date ? new Date((singleData as YoutubeChannel).sync_start_date!).toLocaleDateString() : 'Never'}</div>
                                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                                    <button onClick={handleSync} disabled={syncing} style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>{syncing ? 'Syncing...' : 'Sync Videos'}</button>
                                    <button
                                        onClick={() => {
                                            setViewMode('search');
                                            setSelectedChannel(singleData.youtube_id);
                                            setSearchQuery('');
                                            handleVideoSearch({ channelId: singleData.youtube_id, query: '', offset: 0 });
                                        }}
                                        style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                    >
                                        See Channel Videos
                                    </button>
                                    <button onClick={handleResyncChannel} disabled={loading || syncing} style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>
                                        Resync Channel
                                    </button>
                                    <button onClick={handleDeleteChannel} disabled={loading || syncing} style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', background: '#dc3545', color: 'white', border: 'none' }}>
                                        Delete Channel
                                    </button>
                                </div>
                            </div>
                        )}
                        <p style={{ whiteSpace: 'pre-wrap', marginBottom: '1rem' }}>{singleData.description}</p>
                    </div>
                </div>
                {renderSyncProgress()}
                <details style={{ marginTop: '2rem', background: 'var(--color-bg)', padding: '1rem', borderRadius: '4px' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Raw JSON</summary>
                    <pre style={{ marginTop: '1rem', overflowX: 'auto' }}>{formatJSON(singleData.raw_json)}</pre>
                </details>
            </div>
        );
    };

    const renderTable = () => {
        if (!searchResults || searchResults.videos.length === 0) {
            return searchResults ? <div style={{ marginTop: '2rem' }}>No results found.</div> : null;
        }

        return (
            <div style={{ marginTop: '2rem' }}>
                <VideoTable
                    videos={searchResults.videos}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onVideoClick={fetchVideoDetail}
                    onChannelClick={fetchChannelDetail}
                />

                {/* Pagination */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', marginTop: '2rem' }}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <div>
                            Total Results: <strong>{searchResults.total}</strong>
                        </div>
                        <div>
                            Page Size:
                            <select value={pagination.limit} onChange={handlePageSizeChange} style={{ marginLeft: '0.5rem' }}>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button
                            disabled={currentPage === 1 || loading}
                            onClick={() => handleVideoSearch({ offset: 0 })}
                        >
                            First
                        </button>
                        <button
                            disabled={currentPage === 1 || loading}
                            onClick={() => handleVideoSearch({ offset: Math.max(0, (currentPage - 2) * pagination.limit) })}
                        >
                            Previous
                        </button>

                        <span style={{ margin: '0 0.5rem' }}>
                            Page
                            <input
                                type="number"
                                min={1}
                                max={totalPages}
                                value={currentPage}
                                onChange={(e) => {
                                    const page = parseInt(e.target.value);
                                    if (page >= 1 && page <= totalPages) {
                                        handleVideoSearch({ offset: (page - 1) * pagination.limit });
                                    }
                                }}
                                style={{ width: '50px', marginLeft: '0.5rem', marginRight: '0.5rem' }}
                            />
                            of {totalPages}
                        </span>

                        <button
                            disabled={currentPage === totalPages || loading}
                            onClick={() => handleVideoSearch({ offset: currentPage * pagination.limit })}
                        >
                            Next
                        </button>
                        <button
                            disabled={currentPage === totalPages || loading}
                            onClick={() => handleVideoSearch({ offset: (totalPages - 1) * pagination.limit })}
                        >
                            Last
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderChannels = () => {
        if (!channels || channels.length === 0) {
            return <div style={{ marginTop: '2rem' }}>No channels found.</div>;
        }

        return (
            <div style={{ marginTop: '2rem' }}>
                <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem' }}>
                    <button onClick={handleSelectAll}>Select All</button>
                    <button onClick={handleDeselectAll}>Deselect All</button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '40px', padding: '0.5rem', borderBottom: '1px solid var(--color-border)' }}></th>
                            <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid var(--color-border)' }}>Channel</th>
                            <th style={{ textAlign: 'right', padding: '0.5rem', borderBottom: '1px solid var(--color-border)' }}>Videos</th>
                        </tr>
                    </thead>
                    <tbody>
                        {channels.map((channel: any) => (
                            <tr
                                key={channel.youtube_id}
                                style={{ cursor: 'pointer', borderBottom: '1px solid var(--color-border-dim)' }}
                                className="hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                                <td style={{ padding: '0.5rem' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedChannels.has(channel.youtube_id)}
                                        onChange={(e) => { e.stopPropagation(); toggleChannelSelection(channel.youtube_id); }}
                                    />
                                </td>
                                <td
                                    onClick={() => fetchChannelDetail(channel.youtube_id)}
                                    style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}
                                >
                                    <img
                                        src={channel.thumbnail_url}
                                        alt={channel.title}
                                        style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                                    />
                                    <span>{channel.title}</span>
                                </td>
                                <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                    {channel.video_count?.toLocaleString() || 0}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {selectedChannels.size > 0 && (
                    <div style={{ position: 'sticky', bottom: '1rem', background: 'var(--color-bg)', padding: '1rem', borderTop: '1px solid var(--color-border)', marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 -2px 10px rgba(0,0,0,0.1)' }}>
                        <div>Selected: {selectedChannels.size}</div>
                        {bulkSyncing ? (
                            <div>
                                <strong>{bulkSyncStatus?.progress}</strong>
                                {bulkSyncStatus?.current && <div style={{ fontSize: '0.8rem' }}>Currently syncing: {bulkSyncStatus.current}</div>}
                            </div>
                        ) : (
                            <button onClick={handleBulkSync} style={{ background: 'var(--color-primary)', color: 'white', padding: '0.5rem 1rem', border: 'none', borderRadius: '4px' }}>
                                Sync Selected ({selectedChannels.size})
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <section>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2>YouTube Viewer</h2>
                <div style={{ display: 'flex', gap: '0.5rem', background: 'var(--color-bg)', padding: '0.25rem', borderRadius: '4px' }}>
                    <button
                        onClick={() => setViewMode('id')}
                        style={{ background: viewMode === 'id' ? 'var(--color-primary)' : 'transparent', color: viewMode === 'id' ? 'white' : 'inherit', border: 'none' }}
                    >
                        Fetch ID
                    </button>
                    <button
                        onClick={() => setViewMode('search')}
                        style={{ background: viewMode === 'search' ? 'var(--color-primary)' : 'transparent', color: viewMode === 'search' ? 'white' : 'inherit', border: 'none' }}
                    >
                        Search Database
                    </button>
                    <button
                        onClick={() => { setViewMode('channels'); fetchChannels(); }}
                        style={{ background: viewMode === 'channels' ? 'var(--color-primary)' : 'transparent', color: viewMode === 'channels' ? 'white' : 'inherit', border: 'none' }}
                    >
                        Channels
                    </button>
                </div>
            </div>

            {viewMode === 'id' ? (
                <>
                    <form onSubmit={handleIdSearch} style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                        <select value={type} onChange={(e) => setType(e.target.value as 'channel' | 'video')} style={{ width: '150px' }}>
                            <option value="video">Video</option>
                            <option value="channel">Channel</option>
                        </select>
                        <input type="text" placeholder={type === 'channel' ? "Channel ID" : "Video ID"} value={id} onChange={(e) => setId(e.target.value)} style={{ flex: 1 }} />
                        <button type="submit" disabled={loading || !id}>{loading ? 'Loading...' : 'Fetch'}</button>
                    </form>
                    {error && <div className="error">{error}</div>}
                    {renderSingleResult()}
                </>
            ) : (
                <>
                    <form onSubmit={(e) => { e.preventDefault(); handleVideoSearch({ offset: 0 }); }} style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                        <input
                            type="text"
                            placeholder="Search videos by title..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ flex: 1 }}
                        />
                        <select
                            value={selectedChannel}
                            onChange={(e) => setSelectedChannel(e.target.value)}
                            style={{ width: '200px' }}
                        >
                            <option value="">All Channels</option>
                            {(channels || []).map(c => (
                                <option key={c.youtube_id} value={c.youtube_id}>{c.title}</option>
                            ))}
                        </select>
                        <button type="submit" disabled={loading}>{loading ? 'Searching...' : 'Search'}</button>
                    </form>
                    {error && <div className="error">{error}</div>}
                    {renderTable()}
                </>
            )}
            {viewMode === 'channels' && renderChannels()}
        </section>

    );
}
